import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  HStack,
  VStack,
  useToast,
  Card,
  CardBody,
  IconButton,
  Collapse,
  Skeleton,
  SkeletonCircle,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Avatar,
  useDisclosure,
  Portal,
  FormLabel,
  FormControl,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { SearchIcon, EditIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Filter,
  RefreshCcw,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  DollarSign,
  TrendingUp,
  MoreVertical,
  ViewIcon,
  DeleteIcon,
  Mail,
  ArrowLeft,
  LayoutGrid,
} from "lucide-react";
import {
  getData,
  deleteData,
  updateData,
} from "../../redux/actions/common.action";
import {
  ADMISSION_SECTION_KEYS,
  canDeleteAdmission,
  hasAdmissionSection,
  isApproverDesignation,
} from "../../utils/admissionAccess";
import Pagination from "../proposal/components/Pagination ";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";

// --- Utility: Date Formatter ---
const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const AdmissionList = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const toast = useToast();
  // Delete Modal State
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Mail Modal State
  const {
    isOpen: isMailOpen,
    onOpen: onMailOpen,
    onClose: onMailClose,
  } = useDisclosure();

  const { admissions, loading, branches, courses, accounts, signinuser } = useSelector(
    (state: any) => state.common,
  );

  const canCreateAdmission = hasAdmissionSection(
    signinuser,
    ADMISSION_SECTION_KEYS.CREATE,
  );
  const canEditAdmission =
    hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.EDIT) &&
    !isApproverDesignation(signinuser);
  const canViewAdmission = hasAdmissionSection(
    signinuser,
    ADMISSION_SECTION_KEYS.VIEW,
  );
  const canSendReminder =
    hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.REMINDER) &&
    !isApproverDesignation(signinuser);
  const canOpenBankAccounts = hasAdmissionSection(
    signinuser,
    ADMISSION_SECTION_KEYS.BANK,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [selectedReminderAccountId, setSelectedReminderAccountId] = useState("");

  // --- Filter States ---
  const [branchFilter, setBranchFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Batch wise filtering (session/batch from-to)
  const [batchFrom, setBatchFrom] = useState("");
  const [batchTo, setBatchTo] = useState("");

  // Server totals
  const [overallTotalDocs, setOverallTotalDocs] = useState<number>(0);
  const [filteredTotalDocs, setFilteredTotalDocs] = useState<number>(0);

  const formatBatchValue = (v: string) => {
    if (!v) return "-";
    // Accept both "MM/YYYY" and "YYYY-MM"
    if (/^\d{2}\/\d{4}$/.test(v)) {
      const [mm, yyyy] = v.split("/");
      return `${mm}/${yyyy}`;
    }
    if (/^\d{4}-\d{2}$/.test(v)) {
      const [yyyy, mm] = v.split("-");
      return `${mm}/${yyyy}`;
    }
    return v;
  };

  const fetchAdmissions = useCallback(() => {
    let query = `admission?page=${page}&limit=${limit}`;
    if (search) query += `&search=${search}`;
    if (branchFilter) query += `&branchId=${branchFilter}`;
    if (courseFilter) query += `&courseId=${courseFilter}`;
    if (statusFilter) query += `&admStatus=${statusFilter}`;
    if (modeFilter) query += `&paymentMode=${modeFilter}`;
    if (typeFilter) query += `&paymentType=${typeFilter}`;
    if (startDate) query += `&startDate=${startDate}`;
    if (endDate) query += `&endDate=${endDate}`;
    if (batchFrom) query += `&batchFrom=${batchFrom}`;
    if (batchTo) query += `&batchTo=${batchTo}`;

    dispatch(getData(query)).then((res: any) => {
      const data = res?.data || res;
      if (data?.totalPages !== undefined) {
        setTotalPages(data.totalPages);
      }
      if (data?.totalDocs !== undefined || data?.total !== undefined) {
        setFilteredTotalDocs(data.totalDocs ?? data.total ?? 0);
      }
    });
  }, [
    dispatch,
    page,
    limit,
    search,
    branchFilter,
    courseFilter,
    statusFilter,
    modeFilter,
    typeFilter,
    startDate,
    endDate,
    batchFrom,
    batchTo,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => fetchAdmissions(), 500);
    return () => clearTimeout(handler);
  }, [fetchAdmissions]);

  useEffect(() => {
    dispatch(getData("branch"));
    dispatch(getData("course"));
    dispatch(getData("account"));
  }, [dispatch]);

  // Overall total (no filters) so it stays constant while filters change
  useEffect(() => {
    dispatch(getData(`admission?page=1&limit=${limit}`)).then((res: any) => {
      const data = res?.data || res;
      if (data?.totalDocs !== undefined || data?.total !== undefined) {
        setOverallTotalDocs(data.totalDocs ?? data.total ?? 0);
      }
    });
  }, [dispatch, limit]);

  const handleDelete = async () => {
    if (selectedItem) {
      await dispatch(deleteData(selectedItem._id, "admission", toast));
      onClose();
      fetchAdmissions();
    }
  };

  const handleRefresh = () => {
    setSearch("");
    setBranchFilter("");
    setCourseFilter("");
    setStatusFilter("");
    setModeFilter("");
    setTypeFilter("");
    setStartDate("");
    setEndDate("");
    setBatchFrom("");
    setBatchTo("");
    setPage(1);
    fetchAdmissions();
  };

  const handleSendMail = async () => {
    if (!selectedItem) return;
    if (!selectedReminderAccountId) {
      toast({ title: "Please select a bank account.", status: "warning" });
      return;
    }
    setIsSendingMail(true);

    let payload: any = { paymentAccountId: selectedReminderAccountId };

    if (selectedItem.paymentType === "Full") {
      // Full-payment admission — check the seeded entry's isPaid flag
      const fullEntry = selectedItem.installDetails?.find(
        (e: any) => e.label === "Full Payment",
      );
      if (fullEntry?.isPaid) {
        toast({ title: "Full payment has already been recorded as paid.", status: "info" });
        setIsSendingMail(false);
        onMailClose();
        return;
      }
      // No emiId — backend will handle it as full-payment reminder
    } else {
      // Installment admission — find next unpaid EMI
      const nextPending = selectedItem.installDetails?.find(
        (e: any) => !e.isPaid,
      );
      if (!nextPending) {
        toast({ title: "No pending installments found.", status: "info" });
        setIsSendingMail(false);
        onMailClose();
        return;
      }
      payload.emiId = nextPending._id;
    }

    await dispatch(
      updateData(
        selectedItem._id,
        "admission/send-reminder",
        payload,
        navigate,
        toast,
      ),
    );

    setIsSendingMail(false);
    onMailClose();
  };

  const stats = useMemo(() => {
    const list = Array.isArray(admissions) ? admissions : [];
    return {
      total: list.length,
      approved: list.filter((i: any) => i.admStatus === "Approved").length,
      pending: list.filter(
        (i: any) => !i.admStatus || i.admStatus === "Pending",
      ).length,
      rejected: list.filter((i: any) => i.admStatus === "Rejected").length,
    };
  }, [admissions]);

  // Freeze counts while a status filter is applied,
  // so card numbers don't change after the list refetches.
  const [statsForCards, setStatsForCards] = useState(stats);
  useEffect(() => {
    if (!statusFilter) {
      setStatsForCards(stats);
    }
  }, [statusFilter, stats]);

  type QuickStatus = "" | "Pending" | "Approved" | "Rejected";

  const toggleStatCardFilter = (status: QuickStatus) => {
    setPage(1);
    if (status === "") {
      setStatusFilter("");
      return;
    }
    setStatusFilter((prev) => (prev === status ? "" : status));
  };

  const statCardDefs: {
    filter: QuickStatus;
    label: string;
    hint: string;
    value: number;
    icon: typeof LayoutGrid;
    accent: string;
    gradient: string;
    iconBg: string;
    iconColor: string;
  }[] = [
    {
      filter: "",
      label: "All on page",
      hint: "Clear status filter",
      value: statsForCards.total,
      icon: LayoutGrid,
      accent: "blue",
      gradient: "linear(to-br, blue.500, indigo.600)",
      iconBg: "blue.50",
      iconColor: "#2563eb",
    },
    {
      filter: "Approved",
      label: "Approved",
      hint: "Filter approved",
      value: statsForCards.approved,
      icon: CheckCircle,
      accent: "green",
      gradient: "linear(to-br, emerald.400, teal.600)",
      iconBg: "green.50",
      iconColor: "#059669",
    },
    {
      filter: "Pending",
      label: "Pending",
      hint: "Filter pending",
      value: statsForCards.pending,
      icon: Clock,
      accent: "orange",
      gradient: "linear(to-br, orange.400, amber.500)",
      iconBg: "orange.50",
      iconColor: "#ea580c",
    },
    {
      filter: "Rejected",
      label: "Rejected",
      hint: "Filter rejected",
      value: statsForCards.rejected,
      icon: XCircle,
      accent: "red",
      gradient: "linear(to-br, rose.400, red.600)",
      iconBg: "red.50",
      iconColor: "#e11d48",
    },
  ];

  // --- Modern Shimmer Component ---
  const TableShimmer = () => (
    <>
      {[...Array(limit)].map((_, i) => (
        <Tr key={i}>
          {/* Keep shimmer cell count aligned with table columns */} 
          {[...Array(13)].map((_, j) => (
            <Td key={j}>
              <Skeleton height="20px" borderRadius="md" />
            </Td>
          ))}
        </Tr>
      ))}
    </>
  );

  return (
    <Box p={{ base: 2, md: 6 }} bg="gray.50" minH="100vh">
      <Flex
        align="center"
        justify="space-between"
        flexWrap="wrap"
        gap={3}
        mb={5}
      >
        <HStack spacing={3}>
          <Button
            leftIcon={<ArrowLeft size={18} />}
            variant="ghost"
            colorScheme="gray"
            size="sm"
            fontWeight="semibold"
            onClick={() => navigate("/admission-dashboard")}
            _hover={{ bg: "white", boxShadow: "sm" }}
          >
            Back to dashboard
          </Button>
          {statusFilter ? (
            <Badge
              colorScheme="blue"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              textTransform="none"
            >
              Status filter: <strong>{statusFilter}</strong> — tap the same
              card again to clear
            </Badge>
          ) : null}
        </HStack>

        <VStack align="flex-end" spacing={0}>
          <Text fontSize="xs" color="gray.500">
            Total (overall):{" "}
            <Text as="span" fontWeight="semibold" color="gray.800">
              {overallTotalDocs}
            </Text>
          </Text>
          <Text fontSize="xs" color="gray.500">
            Filtered total:{" "}
            <Text as="span" fontWeight="semibold" color="gray.800">
              {filteredTotalDocs}
            </Text>
          </Text>
          <Text fontSize="xs" color="gray.500">
            On this page:{" "}
            <Text as="span" fontWeight="semibold" color="gray.800">
              {Array.isArray(admissions) ? admissions.length : 0}
            </Text>
          </Text>
        </VStack>
      </Flex>

      {/* 1. Status cards — click to filter / click again to clear */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        {statCardDefs.map((stat) => {
          const active =
            stat.filter === ""
              ? statusFilter === ""
              : statusFilter === stat.filter;
          const IconC = stat.icon;
          return (
            <Box
              key={stat.filter || "all"}
              as="button"
              type="button"
              aria-pressed={active}
              aria-label={`${stat.label}: ${stat.value}. ${stat.hint}`}
              w="100%"
              textAlign="left"
              borderRadius="2xl"
              border="2px solid"
              borderColor={active ? `${stat.accent}.400` : "gray.100"}
              bg={active ? `${stat.accent}.50` : "white"}
              boxShadow={
                active
                  ? "0 12px 40px -12px rgba(0,0,0,0.15)"
                  : "0 4px 20px -4px rgba(0,0,0,0.08)"
              }
              transform={active ? "translateY(-2px)" : undefined}
              transition="all 0.22s ease"
              cursor="pointer"
              position="relative"
              overflow="hidden"
              onClick={() => toggleStatCardFilter(stat.filter)}
              _hover={{
                borderColor: `${stat.accent}.300`,
                boxShadow: "0 16px 48px -12px rgba(0,0,0,0.18)",
                transform: "translateY(-4px)",
              }}
              _active={{ transform: "translateY(-1px)" }}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                h="4px"
                bgGradient={stat.gradient}
                opacity={active ? 1 : 0.65}
              />
              <Box p={5} pt={6}>
                <Flex align="flex-start" justify="space-between" gap={3}>
                  <Box minW={0}>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      color="gray.500"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                    >
                      {stat.label}
                    </Text>
                    <Text
                      fontSize="3xl"
                      fontWeight="extrabold"
                      color="gray.800"
                      lineHeight="1.1"
                      mt={1}
                    >
                      {stat.value}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      {stat.hint}
                    </Text>
                  </Box>
                  <Flex
                    align="center"
                    justify="center"
                    w="52px"
                    h="52px"
                    borderRadius="xl"
                    bg={stat.iconBg}
                    flexShrink={0}
                    boxShadow="inner"
                  >
                    <IconC size={26} color={stat.iconColor} strokeWidth={2} />
                  </Flex>
                </Flex>
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>

      {/* 2. Controls & Filters */}
      <Card variant="outline" mb={4}>
        <CardBody p={4}>
          <Flex
            justify="space-between"
            align="center"
            gap={4}
            direction={{ base: "column", md: "row" }}
          >
            <InputGroup maxW={{ base: "100%", md: "400px" }}>
              <InputLeftElement>
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search Name, Email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <HStack>
              <Button
                leftIcon={<Filter size={16} />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? "solid" : "outline"}
                colorScheme={showFilters ? "blue" : "gray"}
              >
                Filters
              </Button>
              <Button
                leftIcon={<RefreshCcw size={16} />}
                onClick={handleRefresh}
                variant="outline"
              >
                Refresh
              </Button>
              {canCreateAdmission && (
                <Button
                  colorScheme="blue"
                  onClick={() => navigate("/admission-form")}
                >
                  + New Admission
                </Button>
              )}
              {canOpenBankAccounts && (
                <Button
                  colorScheme="blue"
                  onClick={() => navigate("/account-list")}
                >
                  + Accounts
                </Button>
              )}
            </HStack>
          </Flex>
          <Collapse in={showFilters}>
            <SimpleGrid
              columns={{ base: 1, md: 4 }}
              spacing={4}
              mt={4}
              pt={4}
              borderTop="1px solid #eee"
            >
              <FormControl>
                <FormLabel fontSize="xs">Branch</FormLabel>
                <Select
                  placeholder="All Branches"
                  size="sm"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                >
                  {branches?.map((b: any) => (
                    <option key={b._id} value={b._id}>
                      {b.BranchName}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">Course</FormLabel>
                <Select
                  placeholder="All Courses"
                  size="sm"
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  {courses?.map((c: any) => (
                    <option key={c._id} value={c._id}>
                      {c.CourseName}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">Status</FormLabel>
                <Select
                  placeholder="All Status"
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">Payment Mode</FormLabel>
                <Select
                  placeholder="All Modes"
                  size="sm"
                  value={modeFilter}
                  onChange={(e) => setModeFilter(e.target.value)}
                >
                  <option value="Manual">Manual</option>
                  <option value="auto_razorpay">Razorpay</option>
                  <option value="auto_paypal">PayPal</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">Payment Type</FormLabel>
                <Select
                  placeholder="All Types"
                  size="sm"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="Full">Full</option>
                  <option value="Installment">Installment</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">From Date</FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">To Date</FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">Batch From</FormLabel>
                <Input
                  type="month"
                  size="sm"
                  value={batchFrom}
                  onChange={(e) => setBatchFrom(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs">Batch To</FormLabel>
                <Input
                  type="month"
                  size="sm"
                  value={batchTo}
                  onChange={(e) => setBatchTo(e.target.value)}
                />
              </FormControl>
            </SimpleGrid>
          </Collapse>
        </CardBody>
      </Card>

      {/* 3. Data Table */}
      <Card variant="outline" overflow="hidden" boxShadow="sm" mt={6}>
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>Student</Th>
                <Th>Admission ID</Th>
                <Th>Session</Th>
                <Th>Created By</Th>
                <Th>Status</Th>
                <Th>Course</Th>
                <Th>Branch</Th>
                <Th>Counsellor</Th>
                <Th>Payment Config</Th>
                <Th>Collection Status</Th>
                <Th>EMI Milestones</Th>
                <Th>Upcoming EMI</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <TableShimmer />
              ) : (
                (admissions || []).map((item: any) => {
                  const paidFromEmis =
                    item.installDetails
                      ?.filter((e: any) => e.isPaid)
                      .reduce(
                        (acc: number, curr: any) => acc + curr.amount,
                        0,
                      ) || 0;
                  const totalPaid = (item.downPayment || 0) + paidFromEmis;
                  const totalPending = item.totalCoursePrice - totalPaid;
                  const upcoming = item.installDetails?.find(
                    (e: any) => !e.isPaid,
                  );

                  return (
                    <Tr
                      key={item._id}
                      _hover={{ bg: "gray.50" }}
                      transition="background 0.2s"
                    >
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="700" color="gray.700">
                            {item.name}
                          </Text>
                          <Text fontSize="10px" color="gray.500">
                            {item.mobileNo}
                          </Text>
                          <Text
                            fontSize="10px"
                            color="blue.500"
                            isTruncated
                            maxW="120px"
                          >
                            {item.email}
                          </Text>

                        </VStack>
                      </Td>
                      <Td>
                        <Text
                          fontSize="xs"
                          fontFamily="monospace"
                          fontWeight="800"
                          color="gray.700"
                        >
                          {item.admissionId || "-"}
                        </Text>
                      </Td>
                      <Td>
                        {item.sessionFrom || item.sessionTo ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="xs" fontWeight="600" color="purple.600">
                              {formatBatchValue(item.sessionFrom || "")}
                            </Text>
                            <Text fontSize="9px" color="gray.400">to</Text>
                            <Text fontSize="xs" fontWeight="600" color="purple.600">
                              {formatBatchValue(item.sessionTo || "")}
                            </Text>
                          </VStack>
                        ) : (
                          <Text fontSize="xs" color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Avatar
                            size="xs"
                            name={item.createdBy?.Name}
                            boxSize="24px"
                          />
                          <VStack align="start" spacing={-1}>
                            <Text
                              fontSize="10px"
                              fontWeight="700"
                              color="gray.600"
                            >
                              {item.createdBy?.Name}
                            </Text>
                            <Text fontSize="9px" color="gray.400">
                              {formatDate(item.createdAt)}
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <Badge
                          fontSize="xs"
                          py={1}
                          px={2}
                          rounded={"2xl"}
                          colorScheme={
                            item.admStatus === "Approved"
                              ? "green"
                              : item.admStatus === "Pending"
                                ? "yellow"
                                : "red"
                          }
                        >
                          {item.admStatus || "-"}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontWeight="600" fontSize="xs">
                          {item.courseId?.CourseName}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          variant="subtle"
                          colorScheme="purple"
                          fontSize="9px"
                        >
                          {item.branchId?.BranchName}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.600">
                          {item.counsellorId?.Name || "-"}
                        </Text>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" fontWeight="700">
                            Total: ₹{item.totalCoursePrice}
                          </Text>
                          <HStack spacing={1}>
                            <Badge
                              fontSize="9px"
                              colorScheme="blue"
                              variant="solid"
                            >
                              {item.paymentMode}
                            </Badge>
                            <Badge fontSize="9px" variant="outline">
                              {item.paymentType}
                            </Badge>
                          </HStack>
                        </VStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize="xs"
                            fontWeight="700"
                            color="green.600"
                          >
                            Paid: ₹{totalPaid}
                          </Text>
                          <Text fontSize="xs" fontWeight="500" color="red.500">
                            Pend: ₹{totalPending}
                          </Text>
                          {item.downPayment > 0 && (
                            <Text fontSize="9px" color="gray.400">
                              Incl. ₹{item.downPayment} DP
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <HStack spacing={0.5}>
                          {item.installDetails?.map(
                            (step: any, index: number) => {
                              const isPaid = step?.isPaid;
                              const isLast =
                                index === item.installDetails.length - 1;
                              return (
                                <HStack key={step._id} spacing={0}>
                                  <Popover
                                    trigger="hover"
                                    placement="top"
                                    isLazy
                                    border-radius="lg"
                                  >
                                    <PopoverTrigger>
                                      <Box
                                        w="22px"
                                        h="22px"
                                        borderRadius="full"
                                        bg={isPaid ? "green.500" : "gray.200"}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        transition="all 0.2s"
                                        border="1px solid"
                                        borderColor={
                                          isPaid ? "green.600" : "gray.300"
                                        }
                                        cursor="pointer"
                                        _hover={{
                                          transform: "scale(1.2)",
                                          zIndex: 10,
                                        }}
                                      >
                                        <Text
                                          fontSize="9px"
                                          fontWeight="800"
                                          color={isPaid ? "white" : "gray.600"}
                                        >
                                          {index + 1}
                                        </Text>
                                      </Box>
                                    </PopoverTrigger>
                                    <Portal>
                                      <PopoverContent
                                        w="220px"
                                        p={3}
                                        boxShadow="2xl"
                                        zIndex="popover"
                                        borderRadius="xl"
                                      >
                                        <PopoverArrow />
                                        <PopoverBody p={0}>
                                          <VStack align="start" spacing={2}>
                                            <HStack
                                              justify="space-between"
                                              w="full"
                                            >
                                              <Badge
                                                colorScheme={
                                                  isPaid ? "green" : "orange"
                                                }
                                                borderRadius="full"
                                                px={2}
                                              >
                                                {isPaid ? "PAID" : "PENDING"}
                                              </Badge>
                                              <Text
                                                fontSize="10px"
                                                color="gray.400"
                                              >
                                                {step.label}
                                              </Text>
                                            </HStack>
                                            <VStack
                                              align="start"
                                              spacing={0}
                                              w="full"
                                            >
                                              <Text
                                                fontSize="14px"
                                                fontWeight="800"
                                              >
                                                ₹{step.amount}
                                              </Text>
                                              <Text
                                                fontSize="10px"
                                                color="gray.500"
                                              >
                                                Due:{" "}
                                                {formatDate(step.installDate)}
                                              </Text>
                                            </VStack>
                                            {isPaid && (
                                              <Box
                                                w="full"
                                                bg="green.50"
                                                p={2}
                                                borderRadius="md"
                                              >
                                                <Text
                                                  fontSize="9px"
                                                  color="green.700"
                                                  fontWeight="700"
                                                >
                                                  Payment Received On:
                                                </Text>
                                                <Text
                                                  fontSize="10px"
                                                  color="green.600"
                                                >
                                                  {formatDate(step.paidDate)}{" "}
                                                  {step.paidDate
                                                    ? new Date(
                                                        step.paidDate,
                                                      ).toLocaleTimeString(
                                                        "en-GB",
                                                        {
                                                          hour: "2-digit",
                                                          minute: "2-digit",
                                                          hour12: true,
                                                        },
                                                      )
                                                    : ""}
                                                </Text>
                                                {step.remark && (
                                                  <Text
                                                    fontSize="9px"
                                                    color="green.800"
                                                    mt={1}
                                                    fontStyle="italic"
                                                  >
                                                    "{step.remark}"
                                                  </Text>
                                                )}
                                              </Box>
                                            )}
                                          </VStack>
                                        </PopoverBody>
                                      </PopoverContent>
                                    </Portal>
                                  </Popover>
                                  {!isLast && (
                                    <Box
                                      w="10px"
                                      h="2px"
                                      bg={isPaid ? "green.500" : "gray.300"}
                                    />
                                  )}
                                </HStack>
                              );
                            },
                          )}
                        </HStack>
                      </Td>
                      <Td>
                        {upcoming ? (
                          <VStack
                            align="start"
                            spacing={0}
                            bg="orange.50"
                            p={1}
                            borderRadius="md"
                            border="1px solid"
                            borderColor="orange.100"
                          >
                            <Text
                              fontSize="11px"
                              fontWeight="800"
                              color="orange.700"
                            >
                              ₹{upcoming.amount}
                            </Text>
                            <Text fontSize="9px" color="orange.600">
                              {formatDate(upcoming.installDate)}
                            </Text>
                          </VStack>
                        ) : (
                          <Badge colorScheme="green" variant="subtle">
                            Fully Collected
                          </Badge>
                        )}
                      </Td>
                      {/* <Td textAlign="right">
                        <HStack justify="flex-end" spacing={0}>
                          <IconButton
                            aria-label="view"
                            icon={<Eye size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() =>
                              navigate("/view-admission", { state: item })
                            }
                          />
                          <IconButton
                            aria-label="edit"
                            icon={<EditIcon w={4} h={4} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="orange"
                            onClick={() =>
                              navigate("/admission-form", { state: item })
                            }
                          />
                          {signinuser?.UserType === 2 && (
                            <IconButton
                              aria-label="delete"
                              icon={<Trash2 size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => {
                                setSelectedItem(item);
                                onOpen();
                              }}
                            />
                          )}
                        </HStack>
                      </Td> */}
                      <Td textAlign="right">
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            aria-label="Options"
                            icon={<MoreVertical size={16} />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList fontSize="sm">
                            {canViewAdmission && (
                              <MenuItem
                                icon={<ViewIcon />}
                                onClick={() =>
                                  navigate("/view-admission", { state: item })
                                }
                              >
                                View Details
                              </MenuItem>
                            )}

                            {/* Edit: Disabled if Approved */}
                            <MenuItem
                              icon={<EditIcon />}
                              isDisabled={
                                !canEditAdmission || item.admStatus === "Approved"
                              }
                              onClick={() =>
                                navigate("/admission-form", { state: item })
                              }
                            >
                              Edit Admission
                            </MenuItem>

                            {/* Send Mail: Only if Approved */}
                            {item.admStatus === "Approved" && canSendReminder && (
                              <MenuItem
                                icon={<Mail size={14} />}
                                onClick={() => {
                                  setSelectedItem(item);
                                  setSelectedReminderAccountId(
                                    item?.paymentAccount?._id || "",
                                  );
                                  onMailOpen();
                                }}
                              >
                                Send Payment Reminder
                              </MenuItem>
                            )}

                            {/* Delete: company admin or super admin only */}
                            {canDeleteAdmission(signinuser) && (
                              <MenuItem
                                icon={<DeleteIcon />}
                                color="red.500"
                                onClick={() => {
                                  setSelectedItem(item);
                                  onOpen();
                                }}
                              >
                                Delete
                              </MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>

        {/* 4. Pagination & Rows per page */}
        <Box p={4} borderTop="1px solid #eee" bg="white">
          <Flex
            justify="space-between"
            align="center"
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.500">
                Rows per page:
              </Text>
              <Select
                size="xs"
                w="70px"
                borderRadius="md"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
              <Text fontSize="xs" color="gray.400">
                | Showing {admissions?.length || 0} Records
              </Text>
            </HStack>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(p: number) => setPage(p)}
            />
          </Flex>
        </Box>
      </Card>

      <DeleteConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDelete}
        itemName={selectedItem?.name}
        itemType="Admission Record"
      />

      {/* Send Mail Confirmation Modal */}
      <Modal isOpen={isMailOpen} onClose={onMailClose} isCentered size="xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(6px)" />
        <ModalContent
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="gray.100"
          mx={4}
        >
          <ModalHeader
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            color="white"
            borderTopRadius="2xl"
            py={3}
            px={5}
          >
            <HStack spacing={3}>
              <Box
                p={1.5}
                bg="whiteAlpha.200"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Mail size={18} />
              </Box>
              <Text fontSize="md" fontWeight="700">
                Send Payment Reminder
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            top={3}
            right={4}
          />
          <ModalBody py={4} px={5} overflowY="auto" maxH="65vh">
            <VStack spacing={3} align="stretch">
              {/* Student info — compact single row */}
              <Box
                p={3}
                bg="blue.50"
                borderRadius="xl"
                border="1px solid"
                borderColor="blue.100"
              >
                <HStack spacing={3} align="center">
                  <Avatar
                    name={selectedItem?.name}
                    src={selectedItem?.profilePic}
                    size="sm"
                  />
                  <Box flex={1} minW={0}>
                    <Text fontWeight="700" fontSize="sm" color="gray.800" noOfLines={1}>
                      {selectedItem?.name}
                    </Text>
                    <Text fontSize="xs" color="blue.700" noOfLines={1}>
                      📧 {selectedItem?.email}
                    </Text>
                    {selectedItem?.mobileNo && (
                      <Text fontSize="xs" color="gray.500">
                        📱 {selectedItem?.mobileNo}
                      </Text>
                    )}
                  </Box>
                  <Box px={2} py={1} bg="purple.100" borderRadius="full" flexShrink={0}>
                    <Text fontSize="2xs" fontWeight="700" color="purple.700">
                      REMINDER
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {/* Bank Account Selection */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="700" color="gray.700" mb={1}>
                  Receive payment in
                </FormLabel>
                <Select
                  size="sm"
                  value={selectedReminderAccountId}
                  onChange={(e) => setSelectedReminderAccountId(e.target.value)}
                  placeholder="Select bank account"
                  borderRadius="lg"
                >
                  {(accounts || [])
                    ?.filter((a: any) => !a?.isDeleted)
                    ?.map((acc: any) => (
                      <option key={acc._id} value={acc._id}>
                        {acc.bankName} {String(acc.accountNo || "").slice(-4)}
                        {acc.upiId ? ` UPI: ${acc.upiId}` : ""}
                      </option>
                    ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Bank details, UPI ID and QR code will be included in the email.
                </Text>
              </FormControl>

              {/* Info note */}
              <HStack
                spacing={2}
                p={2}
                bg="yellow.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="yellow.200"
              >
                <Text fontSize="sm">💡</Text>
                <Text fontSize="xs" color="gray.700">
                  A professional reminder with the next pending installment details and payment link will be sent.
                </Text>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter
            bg="gray.50"
            borderTop="1px solid"
            borderColor="gray.200"
            borderBottomRadius="2xl"
            py={3}
            px={5}
          >
            <HStack spacing={3} w="100%" justify="flex-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={onMailClose}
                borderRadius="lg"
                fontWeight="600"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                isLoading={isSendingMail}
                onClick={handleSendMail}
                borderRadius="lg"
                fontWeight="600"
                leftIcon={<Mail size={14} />}
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                _hover={{ boxShadow: "md", transform: "translateY(-1px)" }}
                transition="all 0.2s"
              >
                Send Email
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdmissionList;
